<script lang="ts">
import { computed, defineComponent, onBeforeMount } from "vue"
import { useStore } from "@/utils/vuex-utils"
import { useRoute } from "../../utils/router-utils"
import debounce from "lodash/debounce"
import { useI18n } from "@/utils/i18n-utils"

export default defineComponent({
    name: "FilteredSearch",
    setup() {
        const i18n = useI18n()
        const store = useStore()
        const route = useRoute()
        const searchType = route.meta?.searchType as "location" | "device" | "oem-device" | "user"
        const filters = computed(() => store.getters["searchModule/getFilters"](searchType, i18n))

        onBeforeMount(() => {
            store.dispatch("searchModule/initializeSelectedFilters", searchType)
        })

        const searchTerms = computed(() => store.getters["searchModule/getSearchTerms"](searchType))

        const updateSearchTerm = (key: string, value: string) => {
            store.dispatch("searchModule/setSearchTerm", { searchType, key, value })
        }

        const debouncedUpdateMap: Map<string, (value: string) => void> = new Map()

        const getDebouncedUpdate = (key: string) => {
            if (!debouncedUpdateMap.has(key)) {
                debouncedUpdateMap.set(
                    key,
                    debounce((value: string) => {
                        updateSearchTerm(key, value)
                    }, 500),
                )
            }

            return debouncedUpdateMap.get(key)!
        }

        const handleInput = (key: string, event: Event) => {
            const target = event.target as HTMLInputElement | null

            if (target) {
                const value = target.value
                const debouncedUpdate = getDebouncedUpdate(key)
                debouncedUpdate(value)
            }
        }

        return {
            searchTerms,
            filters,
            updateSearchTerm,
            handleInput,
        }
    },
})
</script>

<template>
    <div class="filtered-search-container">
        <h2 class="p-2 heading">{{ $t("Filters") }}</h2>
        <div class="filters-container px-2">
            <div class="filter-item" v-for="(filter, key) in filters" :key="key">
                <div class="mb-3 d-flex flex-column input-group search-input-wrapper w-100">
                    <label class="mb-2 filter-label" :for="`flexCheckDefault-${key}`">
                        {{ filter.label }}
                    </label>
                    <input
                        type="text"
                        class="search-input w-100"
                        autocomplete="nope"
                        placeholder="Search..."
                        :value="searchTerms[key]"
                        @input="handleInput(key.toString(), $event)"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.filtered-search-container {
    display: flex;
    flex-direction: column;
    height: 100%;
}

.heading {
    position: sticky;
    top: 0;
    z-index: 1;
}

.filters-container {
    overflow-y: auto;
    flex-grow: 1;
}

.search-input {
    border: 1px solid var(--theme-secondary);
    border-radius: 0.5rem;
    transition: border-color 0.3s ease;

    &:focus {
        border-color: var(--theme-tertiary);
        outline: none;
        box-shadow: 0 0 5px var(--theme-tertiary);
    }
}

.filter-label {
    color: var(--theme-secondary);
}
</style>
